import React, { useState, useCallback, useRef, useEffect } from 'react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { setUsertokenToCookie } from '@shakkerai/util-cookie';
import { useUserInfoUpdater } from '@shakkerai/pro-store';
import { setUserinfoToLocalStorage } from '@shakkerai/util-localstorage';
import { sendLoginPhoneCode, loginByPhoneCode } from '@shakkerai/services';
import ImgLoginSider from '../assets/login-sider.png';
import './login.scss';

export const AuthModal = (props) => {
  const { onSuccess, className } = props;
  const timer = useRef(null);
  const [phone, setPhone] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [sending, setSending] = useState(false);
  const [logging, setLogging] = useState(false);
  const [count, setCount] = useState(0);

  const { updateUserInfo } = useUserInfoUpdater();

  const handleInputPhone = useCallback((e) => {
    setPhone(e.target.value);
  }, []);

  const handleInputCaptcha = useCallback((e) => {
    setCaptcha(e.target.value);
  }, []);

  const handleSendCaptcha = useCallback(async () => {
    if (sending) return;
    if (!phone) return;

    try {
      setSending(true);
      await sendLoginPhoneCode(phone);

      notifications.show({
        title: '发送成功',
        message: '验证码已发送，请注意查收',
      });

      setCount(60);
      // 开始倒计时
      timer.current = setInterval(() => {
        setCount((prev) => (prev === 1 ? 0 : prev - 1));
      }, 1000);
    } catch (error) {
      notifications.show({
        title: '发送失败',
        message: error.message || '验证码发送失败，请稍后重试',
        color: '#FFBFC5',
      });
    }

    setSending(false);
  }, [sending, phone]);

  const handleLogin = useCallback(async () => {
    if (phone.length !== 11) {
      notifications.show({
        title: '登录失败',
        message: '请输入有效的手机号',
        color: '#FFBFC5',
      });
      return;
    }

    if (captcha.length !== 6) {
      notifications.show({
        title: '登录失败',
        message: '请输入有效的验证码',
        color: '#FFBFC5',
      });
      return;
    }

    try {
      setLogging(true);
      const response = await loginByPhoneCode(phone, captcha);

      if (response && response.data && response.data.token) {
        updateUserInfo({ userInfo: response.data });
        setUsertokenToCookie(response.data.token);
        setUserinfoToLocalStorage(response.data);
      } else {
        throw new Error(response.msg || '登录失败，请稍后重试');
      }

      modals.close('auth-modal');

      notifications.show({
        title: '登录成功',
        message: '欢迎回来',
      });
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: '登录失败',
        message: error.message || '登录失败，请稍后重试',
        color: '#FFBFC5',
      });
    }

    setLogging(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, captcha]);

  useEffect(() => {
    if (count <= 0) {
      clearInterval(timer.current);
    }
  }, [count]);

  useEffect(() => {
    return () => {
      clearInterval(timer.current);
    };
  }, []);

  return (
    <div className={`flex items-stretch w-[766px] h-[584px] overflow-hidden bg-light-500 rounded-xl ${className}`}>
      <img src={ImgLoginSider} className="w-[317px] h-[584px]" />
      <div className="flex flex-col flex-1 items-center p-12">
        <span className="text-2xl text-caption-600 font-semibold">登录</span>
        <span className="mt-1 text-sm text-caption-400">如未注册，验证后自动登录</span>
        <div className="flex items-center relative self-stretch h-14 mt-7">
          <span className="absolute left-4 text-base text-caption-500">+86</span>
          <input
            maxLength={11}
            value={phone}
            onChange={handleInputPhone}
            className="w-full h-14 pl-14 pr-20 bg-caption-100 rounded-md text-base text-caption-500"
            placeholder="请输入手机号"
          />
        </div>
        <div className="flex items-center relative self-stretch h-14 mt-4">
          <input
            maxLength={6}
            value={captcha}
            onChange={handleInputCaptcha}
            className="w-full h-14 pl-4 pr-20 bg-caption-100 rounded-md text-base text-caption-500"
            placeholder="请输入验证码"
          />
          <button
            onClick={handleSendCaptcha}
            disabled={sending || count || phone.length !== 11}
            className="absolute right-2 w-[100px] h-9 bg-transparent border border-primary rounded text-mini text-primary disabled:opacity-50"
          >
            {sending ? '发送中' : count ? `${count}秒后重发` : '发送验证码'}
          </button>
        </div>
        <span className="mt-4 text-mini text-caption-300">
          点击登录，代表同意<span className="text-primary">《用户服务》《隐私条款》</span>
        </span>
        <button
          disabled={logging}
          onClick={handleLogin}
          className="self-stretch h-14 mt-6 bg-primary rounded-md text-lg text-black/80 font-semibold"
        >
          {logging ? '请稍候...' : '登录'}
        </button>
      </div>
    </div>
  );
};
